/**
 * Workspace Super-Agent settings, owned in one place.
 *
 * These settings drive surfaces that are open at the same time as the settings
 * form itself — the composer's thinking control reads the configured model to
 * know which rungs to offer, and tool visibility reads the same payload. Each
 * of those used to fetch on mount and never again, so saving a new model while
 * a chat was open left every one of them describing the previous model until
 * something happened to remount them. Navigating away and back hid it, which is
 * exactly what makes that class of bug survive review.
 *
 * The fix is structural rather than a notification: the WRITE lives here too.
 * A caller cannot update the settings without going through `save`, and `save`
 * publishes the server's response into the same state every reader subscribes
 * to — so there is no event anyone can forget to emit, and no second copy of
 * the payload to drift.
 *
 * @module store/super-agent-settings-store
 */

import { create } from "zustand";
import {
  workspaceService,
  type SuperAgentSettings,
} from "@/services/workspace-service";

/**
 * Where the store is in its lifecycle.
 *
 * `loading` is distinct from "settings are empty": a consumer that cannot tell
 * the two apart renders its default state during the fetch and then snaps to
 * the real one, which is the flicker the skeletons exist to avoid.
 */
export type SuperAgentSettingsStatus = "idle" | "loading" | "ready" | "error";

interface SuperAgentSettingsState {
  settings: SuperAgentSettings | null;
  status: SuperAgentSettingsStatus;
  /**
   * Fetches once and caches. Concurrent callers share the in-flight request
   * rather than each firing their own — several consumers mount together on a
   * chat page, and they would otherwise triple the same call on every load.
   */
  load: () => Promise<void>;
  /** Forces a refetch, ignoring the cache. */
  refresh: () => Promise<void>;
  /**
   * Persists a partial update and publishes the result to every subscriber.
   * The single write path — see the module comment.
   */
  save: (updates: Partial<SuperAgentSettings>) => Promise<SuperAgentSettings>;
  /** Clears cached settings — call on workspace switch or sign-out. */
  reset: () => void;
}

/**
 * The in-flight fetch, shared by concurrent `load` callers. Module-scoped
 * rather than store state because it is plumbing, not something a component
 * should be able to subscribe to or render from.
 */
let inFlight: Promise<void> | null = null;

export const useSuperAgentSettingsStore = create<SuperAgentSettingsState>(
  (set, get) => ({
    settings: null,
    status: "idle",

    load: async () => {
      const { status } = get();
      if (status === "ready") return;
      if (inFlight) return inFlight;
      return get().refresh();
    },

    refresh: async () => {
      set({ status: "loading" });
      inFlight = (async () => {
        try {
          const settings = await workspaceService.getSuperAgentSettings();
          set({ settings, status: "ready" });
        } catch {
          // The service already swallows errors into normalized defaults, so
          // reaching here means something unexpected. Surface it as `error`
          // rather than a permanent `loading`, which would pin every consumer
          // on a skeleton forever.
          set({ status: "error" });
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    },

    save: async (updates) => {
      const settings = await workspaceService.updateSuperAgentSettings(updates);
      // Publish what the server actually persisted, not the optimistic patch:
      // the server merges onto the stored row and may normalize fields, and a
      // consumer acting on the patch would briefly disagree with the backend.
      set({ settings, status: "ready" });
      return settings;
    },

    reset: () => {
      inFlight = null;
      set({ settings: null, status: "idle" });
    },
  }),
);
